import React, { useEffect, useState } from 'react';
import { Result, Typography, Space, Spin } from 'antd';
import { CloudServerOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button } from '@bancobarigui/barihaus';
import { useServiceStatus } from '@/contexts/service-status.context';

const { Text, Title } = Typography;

interface SystemOfflineProps {
  onRetry?: () => void;
}

export function SystemOffline({ onRetry }: SystemOfflineProps): JSX.Element {
  const { checkService, status, lastChecked } = useServiceStatus();
  const [isRetrying, setIsRetrying] = useState(false);
  const [countdown, setCountdown] = useState(30);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          handleRetry();
          return 30;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await checkService('auth');
      if (onRetry) {
        onRetry();
      }
    } finally {
      setIsRetrying(false);
      setCountdown(30);
    }
  };

  const isChecking = status.auth === 'checking' || isRetrying;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f0f2f5',
      }}
    >
      <Result
        icon={
          isChecking ? (
            <Spin size="large" />
          ) : (
            <CloudServerOutlined style={{ color: '#ff4d4f', fontSize: '72px' }} />
          )
        }
        title={
          <Title level={2} style={{ marginBottom: 0 }}>
            Sistema temporariamente indisponivel
          </Title>
        }
        subTitle={
          <Space direction="vertical" size="middle" style={{ marginTop: '16px' }}>
            <Text type="secondary" style={{ fontSize: '16px' }}>
              Estamos trabalhando para restabelecer o servico.
            </Text>
            <Text type="secondary">
              Por favor, aguarde alguns instantes e tente novamente.
            </Text>
            {lastChecked.auth && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Ultima verificacao: {lastChecked.auth.toLocaleTimeString('pt-BR')}
              </Text>
            )}
            {!isChecking && (
              <Text type="secondary" style={{ fontSize: '14px' }}>
                Tentando novamente em {countdown} segundos...
              </Text>
            )}
          </Space>
        }
        extra={
          <Button
            buttonColor="primary"
            size="large"
            icon={<ReloadOutlined spin={isChecking} />}
            onClick={handleRetry}
            loading={isChecking}
            disabled={isChecking}
          >
            {isChecking ? 'Verificando...' : 'Tentar novamente'}
          </Button>
        }
      />
    </div>
  );
}

export default SystemOffline;
